export type HelpSection = { id: string; heading: string; body: string };

export type HelpArticle = {
  slug: string;
  title: string;
  summary: string;
  category: string;
  keywords?: string[];
  sections: HelpSection[];
};

// Display order for category groupings on the /help index.
export const HELP_CATEGORIES: string[] = [
  "Getting started",
  "Deals",
  "Contacts",
  "Credits and billing",
  "Account and team",
  "Privacy",
];

// Body strings use blank-line paragraph breaks. The /help/$slug page
// splits each section.body on /\n\s*\n/ and renders one <p> per chunk,
// preserving single newlines inside a paragraph as soft breaks.
//
// No em dashes anywhere. House rule: do not use "runs" as a
// user-facing noun; use deal, credit, report, sample, member, or
// invitation.

const A_GETTING_STARTED: HelpArticle = {
  slug: "getting-started",
  title: "Getting started",
  summary:
    "Create your account, claim your free first deal, and see what CapitalDart produces before paying.",
  category: "Getting started",
  keywords: ["signup", "first deal", "free", "onboarding", "welcome", "trial"],
  sections: [
    {
      id: "create-account",
      heading: "Create your account",
      body: "Sign up with your work email. There is no credit card on signup. Every new account is provisioned with enough credits to produce one free deal end to end, so you can see the full output before you pay.",
    },
    {
      id: "first-deal",
      heading: "Your first deal",
      body: "From the dashboard, click Start a new deal. Upload your pitch materials (deck, teaser, or memo), give the deal a short name, and submit.\n\nThe pipeline reads your materials, classifies the deal (stage, sector, geography, check size, raise type), filters the investor database against each contact's mandate, ranks the best matches, drafts personalized email and LinkedIn outreach in your voice, and packages the result as an Excel file you can download.\n\nLive status displays while the pipeline works. You do not need to keep the tab open. You can come back later and pick up where you left off.",
    },
    {
      id: "what-you-get",
      heading: "What you get",
      body: "A ranked list of matched investor contacts. Personalized email and LinkedIn copy for each contact, written in your voice. An optional Investor Readiness Report that scores your pitch materials and flags gaps. Optional Financial Model Review, Raise Strategy Report, and Deal Update Report on the same deal. A pipeline view of every contact, with status tracking from first outreach through close.",
    },
    {
      id: "next-steps",
      heading: "Next steps",
      body: "Open the deal page to review matches, send outreach from your own inbox, and track replies by updating each contact's status. The full status set is documented in the contact status legend.",
    },
  ],
};

const A_RUNNING_A_DEAL: HelpArticle = {
  slug: "running-a-deal",
  title: "Running a deal",
  summary:
    "How the pipeline reads your materials, matches investors and acquirers, drafts outreach, and exports the package.",
  category: "Deals",
  keywords: ["pipeline", "deal", "process", "timing", "stages", "excel"],
  sections: [
    {
      id: "what-to-upload",
      heading: "What to upload",
      body: "A deck is the most common input. A teaser, an investor memo, or a fund one-pager also work. The more concrete the document, the better the classification and the match quality.\n\nA text description on its own is not enough to produce a useful match list and is not enough to produce an Investor Readiness Report. Upload at least one document.",
    },
    {
      id: "what-happens",
      heading: "What happens after you submit",
      body: "The pipeline extracts the structure of your deal: stage, sector, geography, check size, business model, traction, raise type. It then filters the investor database against the specific mandate of each contact, scores each match on multiple dimensions, and drafts outreach for the contacts that pass the filter.\n\nDrafts are written in your voice, in an institutional tone. No AI-tells. Declarative and concrete.",
    },
    {
      id: "timing",
      heading: "Timing",
      body: "A typical deal completes in 10 to 30 minutes, depending on how many investors are on the list. A shorter list is faster, and adding the Investor Readiness Report adds a few minutes on top.\n\nYou do not need to wait on the page. The dashboard shows live progress and you will see the deal listed when it is ready.",
    },
    {
      id: "output",
      heading: "The output",
      body: "Inside the app, you get a sortable pipeline view with one row per matched contact, status tracking, and per-contact outreach.\n\nYou can also export the full package as an Excel file with all contacts, firm metadata, and the drafted email and LinkedIn copy ready to send.",
    },
    {
      id: "iterating",
      heading: "Iterating",
      body: "If your materials change, upload the updated version and produce a fresh deal. Past deals stay in your dashboard so you can compare match quality between versions.",
    },
  ],
};

const A_IRR: HelpArticle = {
  slug: "investor-readiness-report",
  title: "The Investor Readiness Report",
  summary:
    "A reviewer-grade read of your pitch materials, with a score, strengths, and the specific gaps to close before investor outreach.",
  category: "Deals",
  keywords: ["IRR", "report", "deck review", "score", "readiness", "feedback"],
  sections: [
    {
      id: "what-it-is",
      heading: "What it is",
      body: "The Investor Readiness Report is a structured read of the pitch materials you uploaded for a deal. It produces a score, a list of strengths, and a prioritized list of gaps with specific suggestions to close each one.\n\nThink of it as a sober reviewer read of your deck before institutional investors see it.",
    },
    {
      id: "what-it-needs",
      heading: "What it needs",
      body: "At least one uploaded document. A deck is best. A teaser, memo, or one-pager also work.\n\nA text description on its own is not enough. The report reviews what is in the document.",
    },
    {
      id: "what-it-covers",
      heading: "What it covers",
      body: "Narrative clarity, problem and solution framing, market sizing, traction evidence, team strength, business model and unit economics as presented, ask and use of proceeds, and how all of that lines up with what institutional investors expect to see in a pitch.",
    },
    {
      id: "what-it-does-not-do",
      heading: "What it does not do",
      body: "The report is not a financial model review. It does not verify projections, audit assumptions, or rebuild your model. It evaluates how the materials present those things, not whether the underlying numbers will hold up in diligence.",
    },
    {
      id: "when-to-use-it",
      heading: "When to use it",
      body: "Before you send your deck out widely. Use the report to find the gaps that an institutional investor will flag in the first read, fix them, then produce another deal so the outreach reflects the stronger materials.",
    },
  ],
};

const A_STATUS_LEGEND: HelpArticle = {
  slug: "status-legend",
  title: "Contact status legend",
  summary:
    "The eight statuses you can set on any contact in a deal, and what each one signals in your pipeline.",
  category: "Contacts",
  keywords: [
    "status",
    "pipeline",
    "legend",
    "invested",
    "prime",
    "interested",
    "awaiting response",
    "not yet contacted",
    "passed",
    "do not send",
  ],
  sections: [
    {
      id: "overview",
      heading: "Overview",
      body: "Every contact on a deal carries a status. The status drives the funnel summary on the dashboard, the pipeline view on the deal page, and the order of contacts in the table.\n\nThe order below runs from best outcome at the top to dead at the bottom.",
    },
    {
      id: "invested",
      heading: "Invested",
      body: "Committed capital to the deal. Use this only after a signed commitment, not for verbal soft circles.",
    },
    {
      id: "prime",
      heading: "Prime",
      body: "Strong live interest, close to a yes. The contact is engaged, the diligence conversation is active, and a decision is near.",
    },
    {
      id: "interested",
      heading: "Interested",
      body: "Signaled interest, not yet committed. A reply has come in, a meeting is on the calendar, or follow-up materials have been requested.",
    },
    {
      id: "awaiting-response",
      heading: "Awaiting response",
      body: "Outreach sent, no reply yet. The default state for any contact you have emailed or messaged on LinkedIn while you wait to hear back.",
    },
    {
      id: "not-yet-contacted",
      heading: "Not yet contacted",
      body: "On the list, not approached. Matched contacts you have not reached out to yet. This is the starting state for newly produced contacts.",
    },
    {
      id: "passed-had-interest",
      heading: "Passed (had interest)",
      body: "Engaged, then declined. The contact responded, considered the deal, and chose not to move forward. Keep these on the list for the next raise; they already know you.",
    },
    {
      id: "passed-no-interest",
      heading: "Passed (no interest)",
      body: "Declined without engaging. A bounce, a polite no, or no signal that the contact ever read the materials.",
    },
    {
      id: "do-not-send",
      heading: "Do not send",
      body: "Excluded. Hold this contact back from outreach for any reason: conflict, prior relationship, contact preference, or a specific instruction from the firm.",
    },
  ],
};

const A_OUTREACH: HelpArticle = {
  slug: "outreach-and-sending",
  title: "Outreach and sending",
  summary:
    "CapitalDart drafts personalized outreach for every matched contact. You send from your own inbox.",
  category: "Contacts",
  keywords: ["outreach", "email", "linkedin", "drafts", "send", "inbox", "mailto"],
  sections: [
    {
      id: "what-gets-drafted",
      heading: "What gets drafted",
      body: "For each matched contact, CapitalDart drafts a personalized email and a personalized LinkedIn message. Drafts are written in your voice and tuned to the specific contact's mandate and the deal's classification, not a generic template.\n\nThe tone is institutional: declarative, concrete, no AI-tells.",
    },
    {
      id: "you-send-from-your-inbox",
      heading: "You send from your own inbox",
      body: "CapitalDart drafts outreach. It sends only when you press Send, from your own address.\n\nFrom any contact, click the send button and your default email client opens with the recipient, subject, and body prefilled. Send from Gmail, Outlook, or whatever you use. The investor sees the message arrive from you, not from a third party.\n\nThis is intentional. Sending from your own inbox keeps deliverability high, keeps your investor relationships under your direct control, and avoids the open and click tracking that institutional investors notice and dislike.",
    },
    {
      id: "linkedin",
      heading: "LinkedIn",
      body: "LinkedIn drafts are provided alongside the email copy. Copy the message, open the contact's LinkedIn profile, and paste. LinkedIn does not allow third-party automated sending for connection requests and InMail, and we do not work around that.",
    },
    {
      id: "tracking-status",
      heading: "Tracking status",
      body: "After you send, set the contact's status to Awaiting response. When a reply comes in, move the contact to Interested, Prime, Invested, or one of the Passed states. The funnel and pipeline views update immediately.",
    },
    {
      id: "bulk-export",
      heading: "Bulk export",
      body: "If you prefer to send from a separate tool you already use, export the deal as an Excel file. Every contact's email address, subject line, body, and LinkedIn message ships in the file, ready to use.",
    },
  ],
};

const A_CREDITS: HelpArticle = {
  slug: "credits-and-billing",
  title: "Credits and billing",
  summary:
    "Credits are prepaid and buy investor contacts. No subscription, no retainer, no tail.",
  category: "Credits and billing",
  keywords: ["credits", "billing", "pricing", "prepaid", "purchase", "balance"],
  sections: [
    {
      id: "how-credits-work",
      heading: "How credits work",
      body: "Credits are prepaid. They buy investor contacts inside a deal. There is no subscription, no monthly minimum, and no retainer.\n\nEach deal you produce draws credits from your balance based on the contacts you unlock. The output is yours to keep. There is no fee at close, no tail, no success fee.\n\nThe Investor Readiness Report is free, and adding or importing your own contacts is free. The optional reports are priced separately: the Financial Model Review at 20 credits, the Raise Strategy Report at 20 credits, and the Deal Update Report at 10 credits, with the first of each free. Credits are used only for the matched investor contacts a deal delivers.",
    },
    {
      id: "your-first-deal-is-free",
      heading: "Your first deal is free",
      body: "Every new account is provisioned with enough credits to produce one deal end to end at no cost. You can review the matched contacts, the outreach drafts, and the Investor Readiness Report before deciding to pay. Your first Financial Model Review, first Raise Strategy Report, and first Deal Update Report are free as well.",
    },
    {
      id: "buying-more",
      heading: "Buying more credits",
      body: "Credits map one-to-one to investor contacts. One credit unlocks one matched contact, with the personalized email draft, the LinkedIn message, and the firm and mandate detail that go with it.\n\nFrom the account menu, click Buy credits to open the billing page. Pick a pack and pay by card. Credits land in your balance immediately and do not expire.\n\nThe current packs are:\n\nStarter. $500 for 100 investor contacts.\n\nGrowth. $1,000 for 250 investor contacts.\n\nScale. $1,750 for 500 investor contacts.\n\nThere is no subscription and no monthly minimum. Credits are prepaid. When you need more, you buy another pack.",
    },
    {
      id: "shared-with-team",
      heading: "Shared with your team",
      body: "If you are on a team, all credits sit in one shared pool that every member draws from. See Teams and seats for details.",
    },
    {
      id: "billing-questions",
      heading: "Billing questions",
      body: "Credits are prepaid and tied to your account balance. If anything about a charge or your balance looks wrong, contact support and we will take a look.",
    },
  ],
};

const A_TEAMS: HelpArticle = {
  slug: "teams-and-seats",
  title: "Teams and seats",
  summary:
    "Invite teammates, share one credit pool, and work on the same deals together.",
  category: "Account and team",
  keywords: ["team", "seats", "invite", "members", "shared credits", "collaborate"],
  sections: [
    {
      id: "invite-a-teammate",
      heading: "Invite a teammate",
      body: "Open the Team page from the account menu. Enter your teammate's email and send the invitation. They will receive a link to create or sign in to their account and join your team.",
    },
    {
      id: "shared-credits",
      heading: "Shared credits",
      body: "All credits sit in one shared pool that anyone on the team draws from. If a teammate had credits in their personal balance before joining, those fold into the shared pool when they accept the invitation.",
    },
    {
      id: "seat-cost",
      heading: "Seat cost",
      body: "Adding a teammate seat costs 10 credits, drawn from the shared pool. The charge happens only when the teammate accepts the invitation, not when you send it. An invitation that is never accepted costs nothing.\n\nOnce a teammate is on the team, they share the same credit pool and see the same deals and the same pipeline as everyone else.",
    },
    {
      id: "what-teammates-see",
      heading: "What teammates see",
      body: "Every member of the team sees the same deals, the same contacts, the same pipeline status, and the same shared credit balance. There are no per-deal permissions; the team operates as one unit.",
    },
    {
      id: "removing-a-member",
      heading: "Removing a member",
      body: "An owner can remove a member from the Team page. The member loses access to the team's deals immediately. Removing a member does not refund credits.",
    },
  ],
};

const A_DATA: HelpArticle = {
  slug: "data-sources",
  title: "Where the investor data comes from",
  summary:
    "A curated institutional database built by investment bankers, covering the full range of alternative capital, not scraped from the web.",
  category: "Getting started",
  keywords: ["database", "data", "coverage", "investors", "sources", "curation"],
  sections: [
    {
      id: "curated-not-scraped",
      heading: "Curated, not scraped",
      body: "The investor database is built and maintained by investment bankers. Contact-level information is verified rather than aggregated from a long list of third-party sources and hoped to be accurate.",
    },
    {
      id: "coverage",
      heading: "Coverage",
      body: "The database covers venture capital, private equity, strategic acquirers, angels and founders, family offices (single and multi), real estate funds, banks, asset managers, corporate VC, hedge funds, specialty lenders, endowments, foundations, insurance companies, pensions, sovereign wealth funds, and infrastructure investors.\n\nThe network is 70,000+ investors, curated rather than scraped.",
    },
    {
      id: "contact-level-not-firm-level",
      heading: "Contact level, not firm level",
      body: "For each contact, the database carries the information you need to make an outreach decision: stage focus, sector focus, geography, check size, and recent investment activity. That is what makes person-level matching possible, not just firm-level filtering.",
    },
    {
      id: "freshness",
      heading: "Freshness",
      body: "The database is updated on an ongoing basis. Contacts who change firms, mandates that shift, and new investment activity are folded in over time, so the matches in a freshly produced deal reflect current coverage.",
    },
  ],
};

const A_COMPARISONS: HelpArticle = {
  slug: "comparisons",
  title: "How CapitalDart compares",
  summary:
    "How CapitalDart fits next to placement agents, investor databases, and other AI capital-raising tools.",
  category: "Getting started",
  keywords: [
    "comparison",
    "alternatives",
    "placement agent",
    "investment bank",
    "pitchbook",
    "preqin",
    "crunchbase",
    "vs",
  ],
  sections: [
    {
      id: "vs-placement-agents",
      heading: "Versus placement agents and investment banks",
      body: "Placement agents and investment banks rely on personal networks built over decades. They charge a percentage of capital raised, a monthly retainer, and a tail on follow-on raises with the same investors. They typically take engagements above a high minimum deal size.\n\nCapitalDart is prepaid software. No placement fee. No retainer. No tail. When the deal closes you keep every basis point. When the next raise closes with the same investors, you keep every basis point on that too.\n\nThis is not a replacement for the senior banker in the room when you need that relationship. It is a replacement for the placement agent process when you can run the outreach yourself, or you want to supplement a placement agent.",
    },
    {
      id: "vs-databases",
      heading: "Versus PitchBook, Preqin, and Crunchbase",
      body: "Databases give you names. They do not match investors to your specific deal, they do not draft the outreach, and they do not track the pipeline through close. They are also expensive on a seat basis.\n\nCapitalDart does the matching against your specific deal, drafts the outreach, and gives you the pipeline view. If you only need raw investor data, a database subscription works. If you need the matching, outreach, and pipeline, CapitalDart replaces all three with one tool.",
    },
    {
      id: "vs-ai-tools",
      heading: "Versus other AI capital-raising tools",
      body: "Most AI capital-raising tools target founders raising VC seed or Series A, with a database that is VC-skewed and a chatbot prose output. Some bundle managed outreach teams that look more like hybrid placement agents than software.\n\nCapitalDart is software-only, prepaid credits, no managed team. The database covers the full range of institutional alternative capital, not just venture. The drafted outreach is institutional in tone, not founder-friendly chatbot prose.",
    },
  ],
};

const A_PRIVACY: HelpArticle = {
  slug: "privacy-and-security",
  title: "Privacy and security",
  summary:
    "Your deal materials, investor list, outreach drafts, and pipeline status stay private to your account.",
  category: "Privacy",
  keywords: ["privacy", "security", "data", "sharing", "training", "confidential"],
  sections: [
    {
      id: "your-deal-is-your-deal",
      heading: "Your deal is your deal",
      body: "Your uploaded materials, the matched investor list, the outreach drafts, and the pipeline status all stay private to your account. We do not sell your data, we do not share it with investors before you reach out, and we do not resell your deal.",
    },
    {
      id: "training",
      heading: "Training",
      body: "We do not use your deal materials to train models. Your deck, memo, or teaser stays inside your account for use in your own deal.",
    },
    {
      id: "deletion",
      heading: "Deletion",
      body: "You can request deletion of a deal or your account at any time from the account menu, or by contacting support.",
    },
    {
      id: "more",
      heading: "More detail",
      body: "The full Privacy Policy and Terms cover the legal specifics. Links to both are in the footer of every page.",
    },
  ],
};

const A_EMAIL_CONNECTIONS: HelpArticle = {
  slug: "email-connections",
  title: "Connect Gmail or Outlook to CapitalDart",
  summary:
    "How email connections work, how to connect Outlook or Gmail, and exactly what CapitalDart reads once a mailbox is connected.",
  category: "Account and team",
  keywords: ["gmail", "outlook", "email", "connect", "replies", "inbox", "permissions"],
  sections: [
    {
      id: "what-connecting-does",
      heading: "What connecting does",
      body: "Connecting your mailbox lets CapitalDart write outreach as you, from your own address, and keep your pipeline current without manual updates.\n\nCapitalDart saves drafts to your Drafts folder, sends only when you press Send or give AgentDart permission to send, and reads replies to outreach you sent through CapitalDart so contact status and notes update themselves. It does not read other mail.",
    },
    {
      id: "connect-outlook",
      heading: "Connect Outlook",
      body: "Open Profile, then App Connections. Click Connect Outlook. Sign in with the Microsoft account you send from. Review the permissions and click Accept. You return to App Connections with the mailbox listed as connected.",
    },
    {
      id: "outlook-admin-approval",
      heading: 'If Outlook says "Need admin approval"',
      body: "Your organization requires IT consent before a new application can use your mailbox. Nothing is wrong with your account.\n\nSend your IT administrator the application name, CapitalDart, and this permission list: Mail.ReadWrite, Mail.Send, User.Read, offline_access. Once they approve it, repeat the connect steps above.",
    },
    {
      id: "connect-gmail",
      heading: "Connect Gmail",
      body: 'Open Profile, then App Connections. Click Connect Gmail and sign in with the Google account you send from.\n\nGoogle may show a screen that says "Google hasn\u2019t verified this app". Click Continue, tick both permissions, then click Continue again.\n\nWhile the application is in testing, only invited Google accounts can connect. If you are blocked, email support@capitaldart.com with the address you want to use and we will add it.',
    },
    {
      id: "reply-tracking",
      heading: "How reply tracking works",
      body: 'A reply matched to a contact on one of your deals updates that contact\u2019s status and adds a short note that begins with "Reply:".\n\nOut-of-office messages and unrelated mail change nothing. Every status is yours to override, so you can change any status by hand at any time.',
    },
    {
      id: "disconnecting",
      heading: "Disconnecting",
      body: "Disconnect from Profile, then App Connections. Reading stops immediately. Drafts already saved in your mailbox stay there, and you can connect again at any time.",
    },
  ],
};

export const HELP_ARTICLES: HelpArticle[] = [
  A_GETTING_STARTED,
  A_RUNNING_A_DEAL,
  A_IRR,
  A_STATUS_LEGEND,
  A_OUTREACH,
  A_CREDITS,
  A_TEAMS,
  A_DATA,
  A_COMPARISONS,
  A_PRIVACY,
  A_EMAIL_CONNECTIONS,
];

export function findArticle(slug: string): HelpArticle | undefined {
  return HELP_ARTICLES.find((a) => a.slug === slug);
}

// Concatenate every searchable string in one article for substring search.
export function articleSearchHaystack(a: HelpArticle): string {
  const parts: string[] = [a.title, a.summary, a.category];
  if (a.keywords) parts.push(a.keywords.join(" "));
  for (const s of a.sections) {
    parts.push(s.heading);
    parts.push(s.body);
  }
  return parts.join(" \n ").toLowerCase();
}

export function groupByCategory(
  articles: HelpArticle[],
): Array<{ heading: string; items: HelpArticle[] }> {
  const map = new Map<string, HelpArticle[]>();
  for (const cat of HELP_CATEGORIES) map.set(cat, []);
  for (const a of articles) {
    if (!map.has(a.category)) map.set(a.category, []);
    map.get(a.category)!.push(a);
  }
  return Array.from(map.entries())
    .filter(([, items]) => items.length > 0)
    .map(([heading, items]) => ({ heading, items }));
}